import { useState, useEffect, useCallback } from 'react';
import {
  checkOutreachReadiness,
  checkConnectionsForChannel,
  checkTestAgentReadiness,
  type ReadinessResult,
  type ConnectionCheckResult,
  type TestReadinessCategory,
} from '../services/readinessService';

const EMPTY_RESULT: ReadinessResult = {
  ready: false,
  loading: true,
  isFirstTime: true,
  setupItems: [],
  agentItems: [],
  connectionItems: [],
  completedCount: 0,
  totalCount: 0,
};

export function useOutreachReadiness(clientId: string | undefined) {
  const [result, setResult] = useState<ReadinessResult>(EMPTY_RESULT);

  const refresh = useCallback(async () => {
    if (!clientId) return;
    setResult(prev => ({ ...prev, loading: true }));
    const data = await checkOutreachReadiness(clientId);
    setResult(data);
  }, [clientId]);

  useEffect(() => {
    if (!clientId) {
      setResult(EMPTY_RESULT);
      return;
    }
    refresh();
  }, [clientId, refresh]);

  return { ...result, refresh };
}

export function useConnectionCheck(clientId: string | undefined) {
  const [checking, setChecking] = useState(false);

  const checkForChannel = useCallback(
    async (channelAction: string): Promise<ConnectionCheckResult> => {
      if (!clientId) return { ready: false, missingConnections: [] };
      setChecking(true);
      try {
        return await checkConnectionsForChannel(clientId, channelAction);
      } finally {
        setChecking(false);
      }
    },
    [clientId],
  );

  return { checkForChannel, checking };
}

type AgentType = 'call' | 'whatsapp' | 'email' | 'linkedin';

export function useTestAgentReadiness(clientId: string | undefined, agentType: AgentType) {
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<TestReadinessCategory[]>([]);

  const check = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const result = await checkTestAgentReadiness(clientId, agentType);
      setReady(result.ready);
      setCategories(result.categories);
    } catch {
      setReady(false);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, agentType]);

  return { ready, loading, categories, check };
}
